import { type MouseEvent, type ReactElement, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useOutlet } from 'react-router-dom';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'motion/react';
import { type ContactDto } from 'xosign-types';
import { MODAL_ANIMATION_DURATION } from '@/config';
import IcCross from '@/assets/icons/Cross.svg?react';
import SuspenseFallback from '@/components/ModalSidebar/SuspenseFallback';
import styles from './ModalSidebar.module.scss';

interface ModalSidebarProps {
  name: string;
  onClose: (data?: ContactDto) => void;
  passOn?: object;
  stackLevel?: number;
}

const ModalSidebar = ({
  name,
  onClose,
  passOn = {},
  stackLevel = 0,
}: ModalSidebarProps) => {
  const { t } = useTranslation(['common']);
  const outletAvailable = useOutlet();

  // Cache the rendered outlet so AnimatePresence can play the exit animation
  // after the route unmounts (avoids "dispatcher is null" in React 19).
  // Using setState-during-render (getDerivedStateFromProps pattern) to keep
  // the cached outlet in sync without refs or effects.
  const [cachedOutlet, setCachedOutlet] = useState<ReactElement | null>(() =>
    outletAvailable ? <Outlet context={{ onClose, ...passOn }} /> : null
  );
  const [prevOutletAvailable, setPrevOutletAvailable] =
    useState(outletAvailable);

  if (outletAvailable !== prevOutletAvailable) {
    setPrevOutletAvailable(outletAvailable);
    if (outletAvailable) {
      setCachedOutlet(<Outlet context={{ onClose, ...passOn }} />);
    }
  }

  const handleClose = (
    event: MouseEvent<HTMLButtonElement | HTMLDivElement>
  ) => {
    onClose();
    event.stopPropagation();
  };

  return (
    <AnimatePresence
      onExitComplete={() => {
        setCachedOutlet(null);
      }}
    >
      {outletAvailable && (
        <motion.div
          key={name}
          className={classNames(
            styles.modal,
            styles[`stackLevel${stackLevel}`]
          )}
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.5 }}
          transition={{
            type: 'tween',
            duration: MODAL_ANIMATION_DURATION,
            ease: 'easeInOut',
          }}
          style={{ transformOrigin: 'center center' }}
        >
          <button
            type="button"
            className={styles.close}
            onClick={handleClose}
            aria-label={t('common:detailView.close')}
          >
            <IcCross className={styles.icon} />
          </button>
          <div className={styles.content}>
            <Suspense fallback={<SuspenseFallback />}>{cachedOutlet}</Suspense>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ModalSidebar;
